<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="contact-overlay"
      @pointerdown="overlayDismiss.onOverlayPointerDown"
      @pointerup="overlayDismiss.onOverlayPointerUp"
    >
      <section class="contact-dialog" role="dialog" aria-modal="true" aria-labelledby="customer-contact-title">
        <header class="contact-header">
          <div>
            <h2 id="customer-contact-title">Contactar al cliente</h2>
            <p>Mensaje desde Kame Seeds</p>
          </div>
          <button type="button" class="contact-close" aria-label="Cerrar" :disabled="sending" @click="close">&times;</button>
        </header>

        <form class="contact-form" @submit.prevent="submit">
          <div class="contact-to">
            <span class="contact-label">Para</span>
            <div class="contact-to-detail">
              <strong>{{ recipientName }}</strong>
              <span>{{ recipientEmail }}</span>
            </div>
          </div>

          <div class="contact-field">
            <label for="customer-contact-order">Pedido</label>
            <select id="customer-contact-order" v-model="selectedOrderId" :disabled="sending || loadingOrders" @change="updateSubject">
              <option v-if="customer" value="">Sin pedido específico</option>
              <option v-for="item in availableOrders" :key="item.id" :value="String(item.id)">
                #{{ item.id }} · {{ item.status_display || item.status }} · {{ formatPrice(item.total) }}
              </option>
            </select>
            <small v-if="loadingOrders" class="contact-note">Cargando pedidos…</small>
            <small v-else-if="order" class="contact-note">
              {{ order.delivery_type_display || (order.delivery_type === 'pickup' ? 'Acordar envío' : 'Correo Argentino') }}
            </small>
          </div>

          <div class="contact-field">
            <label for="customer-contact-subject">Asunto</label>
            <input id="customer-contact-subject" v-model.trim="subject" type="text" maxlength="180" required :disabled="sending" placeholder="Asunto del email">
          </div>

          <div class="contact-field">
            <label for="customer-contact-message">Mensaje</label>
            <textarea id="customer-contact-message" ref="messageInput" v-model.trim="message" rows="5" minlength="10" maxlength="5000" required :disabled="sending" placeholder="Escribí el mensaje para el cliente..."></textarea>
            <small class="contact-note">Mínimo 10 caracteres</small>
          </div>

          <p v-if="error" class="contact-error" role="alert">{{ error }}</p>

          <footer class="contact-actions">
            <button type="button" class="btn btn-outline-light btn-sm" :disabled="sending" @click="close">Cancelar</button>
            <button type="submit" class="btn btn-primary btn-sm" :disabled="sending || !recipientEmail || !subject.trim() || message.trim().length < 10">
              <i :class="sending ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane'" class="me-2"></i>{{ sending ? 'Enviando…' : 'Enviar email' }}
            </button>
          </footer>
        </form>
      </section>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, nextTick, onUnmounted } from 'vue'
import { useToast } from 'vue-toastification'
import { sendCustomerContact, getContactOrders } from '@/services/customerContact'
import { getOrderCustomerName } from '@/utils/orderDisplay'
import { useModalOverlayDismiss } from '@/composables/useModalOverlayDismiss'

const toast = useToast()
const visible = ref(false)
const customer = ref(null)
const availableOrders = ref([])
const selectedOrderId = ref('')
const loadingOrders = ref(false)
const subject = ref('')
const message = ref('')
const error = ref('')
const sending = ref(false)
const messageInput = ref(null)
let previousOverflow = ''
let requestVersion = 0

const formatPrice = (value) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value || 0)
const order = computed(() => availableOrders.value.find((item) => String(item.id) === selectedOrderId.value) || null)
const recipientName = computed(() => {
  if (order.value) return getOrderCustomerName(order.value) || order.value.recipient_name || 'Cliente'
  const value = customer.value
  return [value?.first_name, value?.last_name].filter(Boolean).join(' ') || value?.username || 'Cliente'
})
const recipientEmail = computed(() => order.value?.customer_email || customer.value?.email || '')

const updateSubject = () => {
  const value = order.value
  subject.value = value
    ? `Pedido #${value.id} · ${value.status_display || value.status} · ${formatPrice(value.total)} | Kame Seeds`.slice(0, 180)
    : 'Kame Seeds · Nos ponemos en contacto'
}

const loadOrders = async (userId, email) => {
  if (!userId && !email) return
  const version = ++requestVersion
  loadingOrders.value = true
  try {
    const items = await getContactOrders(userId, email)
    if (version !== requestVersion) return
    const byId = new Map(availableOrders.value.map((item) => [String(item.id), item]))
    items.forEach((item) => byId.set(String(item.id), item))
    availableOrders.value = [...byId.values()].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
  } catch {
    // El pedido de origen sigue disponible aunque falle la carga de otros pedidos.
  } finally {
    if (version === requestVersion) loadingOrders.value = false
  }
}

const show = async () => {
  error.value = ''
  message.value = ''
  previousOverflow = document.body.style.overflow
  document.body.style.overflow = 'hidden'
  visible.value = true
  document.addEventListener('keydown', onKeydown)
  await nextTick()
  messageInput.value?.focus()
}

const openForOrder = (value) => {
  customer.value = null
  availableOrders.value = [value]
  selectedOrderId.value = String(value.id)
  updateSubject()
  show()
  loadOrders(value.user?.id || value.user, value.customer_email)
}

const openForCustomer = (value, orders = []) => {
  customer.value = value
  availableOrders.value = orders
  selectedOrderId.value = ''
  updateSubject()
  show()
  loadOrders(value.id, value.email)
}

const close = () => {
  if (sending.value) return
  requestVersion += 1
  visible.value = false
  document.body.style.overflow = previousOverflow
  document.removeEventListener('keydown', onKeydown)
}

const overlayDismiss = useModalOverlayDismiss(close)

const onKeydown = (event) => {
  if (event.key === 'Escape') close()
}

const submit = async () => {
  if (sending.value) return
  sending.value = true
  error.value = ''
  try {
    const target = order.value ? { order_id: order.value.id } : { user_id: customer.value.id }
    await sendCustomerContact(target, subject.value.trim(), message.value.trim())
    sending.value = false
    close()
    toast.success('Email enviado al cliente')
  } catch (err) {
    error.value = err.response?.data?.detail || 'No se pudo enviar el email. Intentá nuevamente.'
    sending.value = false
  }
}

onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown)
  if (visible.value) document.body.style.overflow = previousOverflow
})

defineExpose({ openForOrder, openForCustomer })
</script>

<style scoped>
.contact-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: grid;
  place-items: center;
  padding: 12px;
  background: rgba(0, 0, 0, .76);
}
.contact-dialog {
  width: min(100%, 480px);
  max-height: 92vh;
  overflow-y: auto;
  background: #1b1b1b;
  color: #eee;
  border: 1px solid #393939;
  border-radius: 12px;
  box-shadow: 0 18px 55px rgba(0, 0, 0, .55);
  font-family: "Poppins", "Helvetica Neue", Helvetica, Arial, sans-serif;
}
.contact-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px 14px;
  border-bottom: 1px solid #303030;
}
.contact-header h2 { margin: 0 0 3px; color: #fff; font-size: 1.1rem; font-weight: 700; }
.contact-header p { margin: 0; color: #999; font-size: .8rem; }
.contact-close { border: 0; background: transparent; color: #aaa; font-size: 1.6rem; line-height: 1; padding: 0 2px; cursor: pointer; }
.contact-close:hover { color: #fff; }
.contact-form { display: flex; flex-direction: column; gap: 14px; padding: 18px 22px 20px; }
.contact-to {
  display: grid;
  grid-template-columns: 58px minmax(0, 1fr);
  gap: 8px;
  align-items: center;
  padding: 0 0 13px;
  border-bottom: 1px solid #303030;
}
.contact-label { color: #aaa; font-size: .82rem; }
.contact-to-detail { display: flex; flex-direction: column; min-width: 0; line-height: 1.3; overflow-wrap: anywhere; }
.contact-to-detail strong { color: #fff; font-size: .91rem; font-weight: 600; }
.contact-to-detail span { color: #aaa; font-size: .8rem; }
.contact-field { display: flex; flex-direction: column; gap: 6px; }
.contact-field label { color: #ddd; font-size: .84rem; font-weight: 600; }
.contact-field input, .contact-field select, .contact-field textarea {
  width: 100%;
  min-height: 39px;
  padding: 9px 11px;
  background: #111;
  color: #fff;
  border: 1px solid #454545;
  border-radius: 7px;
  outline: none;
  font-size: .9rem;
}
.contact-field input:focus, .contact-field select:focus, .contact-field textarea:focus {
  border-color: #ffec00;
  box-shadow: 0 0 0 2px rgba(255, 236, 0, .12);
}
.contact-field textarea { min-height: 115px; resize: vertical; }
.contact-note { color: #999; font-size: .75rem; line-height: 1.3; }
.contact-error { margin: 0; color: #ffb4b4; font-size: .83rem; }
.contact-actions { display: flex; justify-content: flex-end; gap: 8px; padding-top: 2px; }
@media (max-width: 575px) {
  .contact-header { padding: 16px 17px 13px; }
  .contact-form { padding: 16px 17px 18px; }
  .contact-actions .btn { flex: 1; }
}
</style>
